import { BRAND } from '../../../shared/config/brand'

export type FacilityStatus =
  | 'Active'
  | 'In arrears'
  | 'Closing soon'
  | 'Paid off'

export interface Facility {
  id: string
  principal: number
  totalRepayment: number
  outstanding: number
  repaidPercent: number
  holdbackRate: number
  startDate: string
  projectedCloseDate: string
  status: FacilityStatus
  avgDailyDeduction: number
  pricingModel: string
  disbursementAccount: string
  targetRepaymentWindow: string
}

export const mockFacilities: Facility[] = [
  {
    id: 'CIMB-SME-00417',
    principal: 130000,
    totalRepayment: 149500,
    outstanding: 47840,
    repaidPercent: 68,
    holdbackRate: 10,
    startDate: 'Jan 2026',
    projectedCloseDate: 'Oct 2026',
    status: 'Active',
    avgDailyDeduction: 500,
    pricingModel: 'Fixed factor rate ×1.15',
    disbursementAccount: `${BRAND.name} •••• 4521`,
    targetRepaymentWindow: '9 months',
  },
  {
    id: 'CIMB-SME-00392',
    principal: 50000,
    totalRepayment: 57500,
    outstanding: 0,
    repaidPercent: 100,
    holdbackRate: 10,
    startDate: 'Jul 2025',
    projectedCloseDate: 'Dec 2025',
    status: 'Paid off',
    avgDailyDeduction: 250,
    pricingModel: 'Fixed factor rate ×1.15',
    disbursementAccount: `${BRAND.name} •••• 4521`,
    targetRepaymentWindow: '6 months',
  },
  {
    id: 'CIMB-SME-00210',
    principal: 100000,
    totalRepayment: 115000,
    outstanding: 0,
    repaidPercent: 100,
    holdbackRate: 15,
    startDate: 'Jan 2025',
    projectedCloseDate: 'Jun 2025',
    status: 'Paid off',
    avgDailyDeduction: 400,
    pricingModel: 'Fixed factor rate ×1.15',
    disbursementAccount: `${BRAND.name} •••• 4521`,
    targetRepaymentWindow: '6 months',
  },
  {
    id: 'CIMB-SME-00450',
    principal: 200000,
    totalRepayment: 230000,
    outstanding: 200100,
    repaidPercent: 13,
    holdbackRate: 10,
    startDate: 'Mar 2026',
    projectedCloseDate: 'Mar 2027',
    status: 'In arrears',
    avgDailyDeduction: 150,
    pricingModel: 'Fixed factor rate ×1.15',
    disbursementAccount: `${BRAND.name} •••• 4521`,
    targetRepaymentWindow: '12 months',
  },
]

export function getFacilityById(id: string): Facility | undefined {
  return mockFacilities.find((f) => f.id === id)
}

/**
 * A flexible financing offer the merchant can use to pay a batch of bills.
 * Offers trade off three dimensions:
 *  - termMonths: longer term = smaller daily impact but higher total cost
 *  - holdbackRate: % of daily settlement diverted to repayment
 *  - factorRate: fixed-fee multiplier applied to the financed principal
 */
export interface FinancingOffer {
  id: string
  name: string
  /** Short marketing tagline, e.g. "Lightest on daily cash flow". */
  tagline: string
  termMonths: number
  holdbackRate: number
  factorRate: number
  /** Whether this is the recommended/default offer. */
  recommended?: boolean
  /** The single biggest selling point, surfaced as a highlight chip. */
  highlight: string
}

export interface ComputedOffer extends FinancingOffer {
  principal: number
  totalRepayable: number
  totalFees: number
  /** Effective fee as a % of principal. */
  feePercent: number
  /** Rough estimated daily repayment over the term. */
  estimatedDailyDeduction: number
}

export const financingOffers: FinancingOffer[] = [
  {
    id: 'flexi-12',
    name: 'Flexi 12',
    tagline: 'Lightest on daily cash flow',
    termMonths: 12,
    holdbackRate: 8,
    factorRate: 1.18,
    highlight: 'Lowest daily deduction',
  },
  {
    id: 'balanced-9',
    name: 'Balanced 9',
    tagline: 'A balance of speed and cost',
    termMonths: 9,
    holdbackRate: 10,
    factorRate: 1.15,
    recommended: true,
    highlight: 'Most popular',
  },
  {
    id: 'saver-6',
    name: 'Saver 6',
    tagline: 'Pay less overall, repay sooner',
    termMonths: 6,
    holdbackRate: 14,
    factorRate: 1.11,
    highlight: 'Lowest total cost',
  },
]

/** Computes the cost breakdown of each offer for a given financed amount. */
export function getFinancingOffers(principal: number): ComputedOffer[] {
  return financingOffers.map((offer) => {
    const totalRepayable = principal * offer.factorRate
    const totalFees = totalRepayable - principal
    const feePercent = (totalFees / principal) * 100
    const estimatedDailyDeduction = totalRepayable / (offer.termMonths * 30)
    return {
      ...offer,
      principal,
      totalRepayable,
      totalFees,
      feePercent,
      estimatedDailyDeduction,
    }
  })
}

export function getOfferById(id: string): FinancingOffer | undefined {
  return financingOffers.find((o) => o.id === id)
}

/* ------------------------------------------------------------------ *
 * Apply-wizard underwriting data: eligibility gates + GMV signals.
 * Drives the "Apply for financing" flow in the Financing section.
 * ------------------------------------------------------------------ */

export interface EligibilityGate {
  id: string
  label: string
  /** Human-readable requirement, e.g. "≥ 6 months". */
  requirement: string
  /** The merchant's current value for this gate. */
  current: string
  passed: boolean
}

export interface GmvSignal {
  id: string
  label: string
  value: string
  /** Optional supporting detail / interpretation. */
  detail: string
  sentiment: 'positive' | 'neutral' | 'caution'
}

export interface FinancingEligibility {
  /** Whether all gates pass (instant approval available). */
  eligible: boolean
  /** Underwriting score out of 100. */
  score: number
  /** Tier label the score maps to, e.g. "Gold". */
  scoreTier: string
  /** Maximum pre-approved principal. */
  maxAmount: number
  gates: EligibilityGate[]
  signals: GmvSignal[]
  /** Trailing-90-day daily GMV series for the analysis chart (MYR). */
  gmvSeries: number[]
}

export const financingEligibility: FinancingEligibility = {
  eligible: true,
  score: 84.2,
  scoreTier: 'Gold',
  maxAmount: 150000,
  gates: [
    {
      id: 'tenure',
      label: 'Time on platform',
      requirement: '≥ 6 months',
      current: '11 months',
      passed: true,
    },
    {
      id: 'volume',
      label: 'Avg. monthly volume',
      requirement: '≥ MYR 50,000',
      current: 'MYR 92,400',
      passed: true,
    },
    {
      id: 'chargebacks',
      label: 'Chargeback rate',
      requirement: '< 0.5%',
      current: '0.1%',
      passed: true,
    },
    {
      id: 'standing',
      label: 'Account standing',
      requirement: 'Good standing',
      current: 'Good standing',
      passed: true,
    },
    {
      id: 'kyb',
      label: 'Business verification (KYB)',
      requirement: 'Verified',
      current: 'Verified',
      passed: true,
    },
  ],
  signals: [
    {
      id: 'gmv-90d',
      label: 'Trailing 90-day GMV',
      value: 'MYR 277,200',
      detail: 'Up 18% vs prior 90 days',
      sentiment: 'positive',
    },
    {
      id: 'avg-daily',
      label: 'Avg. daily settlement',
      value: 'MYR 3,080',
      detail: 'Stable across the period',
      sentiment: 'positive',
    },
    {
      id: 'volatility',
      label: 'Revenue volatility',
      value: 'Low',
      detail: 'Consistent week-on-week settlements',
      sentiment: 'positive',
    },
    {
      id: 'refunds',
      label: 'Refund rate',
      value: '1.4%',
      detail: 'Within healthy range for your category',
      sentiment: 'neutral',
    },
    {
      id: 'seasonality',
      label: 'Seasonality',
      value: 'Mild upcoming peak',
      detail: 'Festive demand expected next quarter',
      sentiment: 'neutral',
    },
  ],
  // 90 points of synthetic daily GMV with a gentle upward trend + noise.
  gmvSeries: Array.from({ length: 90 }, (_, i) => {
    const trend = 2400 + i * 9
    const wave = Math.sin(i / 6) * 350
    const noise = ((i * 53) % 11) * 40 - 200
    return Math.max(800, Math.round(trend + wave + noise))
  }),
}

export type SettlementSpeed = 'instant' | 'next_day'

export interface SettlementOption {
  id: SettlementSpeed
  label: string
  description: string
  /** Fee for this disbursement speed, as a flat MYR amount. */
  fee: number
  eta: string
}

export const settlementOptions: SettlementOption[] = [
  {
    id: 'instant',
    label: 'Instant payout',
    description:
      'Funds hit your settlement account within minutes of approval.',
    fee: 50,
    eta: 'Within minutes',
  },
  {
    id: 'next_day',
    label: 'Next-day settlement',
    description: 'Disbursed with your next settlement batch — no extra fee.',
    fee: 0,
    eta: 'Next business day',
  },
]

export interface LedgerRow {
  id: string
  date: string
  batchRef: string
  gmv: number
  deducted: number
  runningBalance: number
}

export function generateRepaymentLedger(facility: Facility): LedgerRow[] {
  const rows: LedgerRow[] = []
  let currentBalance = facility.totalRepayment
  const targetBalance = facility.outstanding
  const daysToGenerate = 60

  // Start from today and go backwards
  const today = new Date('2026-05-14T00:00:00Z') // Using the prototype's current context date

  for (let i = 0; i < daysToGenerate; i++) {
    if (currentBalance <= targetBalance) break

    const date = new Date(today)
    date.setDate(date.getDate() - i)

    // Vary GMV to show the flexible mechanic. Every shown day has real
    // settlement activity — non-trading days are simply not listed, so a row
    // never reads as a missed deduction.
    const baseGmv = facility.avgDailyDeduction / (facility.holdbackRate / 100)
    const variance = 0.8 + Math.random() * 0.4 // ±20%
    const dailyGmv = baseGmv * variance

    const deducted = dailyGmv * (facility.holdbackRate / 100)

    // Don't deduct more than what's needed to reach target
    const actualDeducted = Math.min(deducted, currentBalance - targetBalance)

    if (actualDeducted > 0) {
      currentBalance -= actualDeducted

      rows.push({
        id: `ledg_${i}`,
        date: date.toISOString(),
        batchRef: `CIMB-${date.toISOString().split('T')[0].replace(/-/g, '')}-TXN-${Math.floor(1000 + Math.random() * 9000)}`,
        gmv: dailyGmv,
        deducted: actualDeducted,
        runningBalance: currentBalance,
      })
    }
  }

  return rows
}
